import type { LanguageDetectorModule } from 'i18next';

/**
 * Custom language detector that reads the user's language preference
 * from sessionStorage (where OIDC userStore may store user data).
 * Since the OIDC provider has no `locale` field, this detector tries
 * to read from sessionStorage and falls through if nothing is found.
 *
 * Maps detected codes: zh -> zh-CN, en -> en-US
 */
const storageDetector: LanguageDetectorModule = {
  type: 'languageDetector',
  name: 'storageDetector',

  detect() {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Try to read from sessionStorage (OIDC userStore may have stored locale info)
    try {
      const stored = sessionStorage.getItem('i18nextLng');
      if (stored) {
        const normalized = this.normalizeCode(stored);
        if (normalized) return normalized;
      }
    } catch {
      // sessionStorage might not be available
    }

    return undefined;
  },

  cacheUserLanguage(lng: string) {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem('i18nextLng', lng);
    } catch {
      // sessionStorage might not be available
    }
  },

  normalizeCode(code: string): string | undefined {
    const lower = code.toLowerCase();
    if (lower === 'zh' || lower === 'zh-cn' || lower === 'zh-hans' || lower === 'zh-hans-cn') {
      return 'zh-CN';
    }
    if (lower === 'en' || lower === 'en-us' || lower === 'en-gb') {
      return 'en-US';
    }
    return undefined;
  },
};

export default storageDetector;
